/**
 * Result of flattening an object with path segments for each field.
 *
 * The segments map stores the original map structure for each flattened field name,
 * which is used to properly escape periods in field names when building queries.
 *
 * Example:
 * - Input: {output: {"double.nested": "hello"}}
 * - flattened: {"output.double.nested": "hello"}
 * - segments: {"output.double.nested": ["output", "double.nested"]}
 */
export interface FlattenedWithSegments {
  flattened: {[key: string]: any};
  segments: Record<string, string[]>;
}

/**
 * Flatten an object, but preserve any object that has a `_type` field.
 * This is critical for handling "Weave Types" - payloads that should be
 * treated as holistic objects, rather than flattened.
 *
 * @returns Object with flattened data and path segments for each field
 */
export const flattenObjectPreservingWeaveTypes = (obj: {
  [key: string]: any;
}): FlattenedWithSegments => {
  return flattenObject(obj, (key, value) => {
    return !isCustomWeaveType(value);
  });
};

export const isCustomWeaveType = (value: any) => {
  return (
    typeof value === 'object' &&
    value !== null &&
    value._type === 'CustomWeaveType'
  );
};

export const flattenObject = (
  obj: {[key: string]: any},
  shouldFlatten: (key: string, value: any) => boolean = () => true
): FlattenedWithSegments => {
  const flattened: {[key: string]: any} = {};
  const segments: Record<string, string[]> = {};
  _flattenObject(obj, '', flattened, segments, [], shouldFlatten);
  return {flattened, segments};
};

const _flattenObject = (
  obj: {[key: string]: any},
  parentKey: string = '',
  result: {[key: string]: any} = {},
  segments: Record<string, string[]> = {},
  pathStack: string[] = [],
  shouldFlatten: (key: string, value: any) => boolean = () => true
) => {
  if (
    typeof obj !== 'object' ||
    obj === null ||
    !shouldFlatten(parentKey, obj)
  ) {
    return obj;
  }
  const keys = Object.keys(obj);
  keys.forEach(key => {
    if (!obj.hasOwnProperty(key)) {
      return;
    }
    const newKey = parentKey ? `${parentKey}.${key}` : key;
    const newPath = [...pathStack, key];

    if (Array.isArray(obj[key])) {
      result[newKey] = obj[key];
      segments[newKey] = newPath;
    } else if (typeof obj[key] === 'object' && shouldFlatten(key, obj[key])) {
      _flattenObject(
        obj[key],
        newKey,
        result,
        segments,
        newPath,
        shouldFlatten
      );
    } else {
      result[newKey] = obj[key];
      // Store original path segments for this field, respecting escaped delimiters
      segments[newKey] = newPath;
    }
  });
  return result;
};
